import { supabase } from '../integrations/supabase/client'

export interface DocumentChangeMessage {
  msg_id: number
  document_id: number
  change_type: 'created' | 'updated' | 'deleted'
  timestamp: number
  changed_by: string
  old_data: any
  new_data: any
  read_ct: number
  enqueued_at: string
}

export interface QueueStats {
  queue_name: string
  queue_length: number
  newest_msg_age_sec: number
  oldest_msg_age_sec: number
  total_messages: number
}

export class DocumentQueueManager {
  /**
   * Manually send a document change message to the queue
   * (Usually this happens automatically via database triggers)
   */
  static async sendDocumentChange(
    documentId: number,
    changeType: 'created' | 'updated' | 'deleted',
    oldData?: any,
    newData?: any,
    changedBy?: string
  ): Promise<number> {
    const { data, error } = await supabase.rpc('send_document_change', {
      document_id: documentId,
      change_type: changeType,
      old_data: oldData || null,
      new_data: newData || null,
      changed_by: changedBy || null
    })

    if (error) {
      throw new Error(`Failed to send document change message: ${error.message}`)
    }

    return data as number
  }

  /**
   * Read document change messages from the queue
   * WARNING: This should only be used for monitoring/display purposes
   * Queue processing should happen server-side via edge functions
   */
  static async readDocumentChanges(
    visibilityTimeoutSeconds: number = 30,
    maxMessages: number = 1
  ): Promise<DocumentChangeMessage[]> {
    const { data, error } = await supabase.rpc('read_document_changes', {
      visibility_timeout_seconds: visibilityTimeoutSeconds,
      max_messages: maxMessages
    })

    if (error) {
      throw new Error(`Failed to read document changes: ${error.message}`)
    }

    return data as DocumentChangeMessage[]
  }

  /**
   * Delete a processed message from the queue
   */
  static async deleteMessage(messageId: number): Promise<boolean> {
    const { data, error } = await supabase.rpc('delete_document_change_message', {
      message_id: messageId
    })

    if (error) {
      throw new Error(`Failed to delete message: ${error.message}`)
    }

    return data as boolean
  }

  /**
   * Archive a processed message (moves it to archive table)
   */
  static async archiveMessage(messageId: number): Promise<boolean> {
    const { data, error } = await supabase.rpc('archive_document_change_message', {
      message_id: messageId
    })

    if (error) {
      throw new Error(`Failed to archive message: ${error.message}`)
    }

    return data as boolean
  }

  /**
   * Get queue statistics
   */
  static async getQueueStats(): Promise<QueueStats[]> {
    const { data, error } = await supabase
      .from('document_queue_stats')
      .select('*')

    if (error) {
      throw new Error(`Failed to get queue stats: ${error.message}`)
    }

    return data as QueueStats[]
  }

  /**
   * Trigger the server-side edge function consumer to process messages
   * This is the recommended way to process queue messages
   */
  static async triggerConsumer(batchSize: number = 5, visibilityTimeout: number = 30) {
    const { data, error } = await supabase.functions.invoke('document-queue-consumer', {
      method: 'POST',
      body: {},
      headers: {
        'Content-Type': 'application/json'
      }
    })

    if (error) {
      throw new Error(`Failed to trigger consumer: ${error.message}`)
    }

    return data
  }

  /**
   * Start a client-side polling consumer (for testing purposes only)
   * WARNING: This should NOT be used in production. Use triggerConsumer() instead.
   * Multiple clients polling for messages can cause race conditions and inefficient processing.
   */
  static startPollingConsumer(
    intervalMs: number = 5000,
    batchSize: number = 5,
    onMessage?: (messages: DocumentChangeMessage[]) => void,
    onError?: (error: Error) => void
  ): () => void {
    let isRunning = true

    console.warn('⚠️ Client-side consumer started - this should only be used for testing!')

    const poll = async () => {
      try {
        if (!isRunning) return

        const messages = await this.readDocumentChanges(30, batchSize)
        
        if (messages.length > 0) {
          console.log(`Processing ${messages.length} document change messages`)
          
          // Call the callback if provided
          if (onMessage) {
            onMessage(messages)
          } else {
            // Default processing: just log
            messages.forEach(message => {
              console.log(`Document ${message.document_id} ${message.change_type} by ${message.changed_by}`)
              console.log('Message details:', message)
            })
          }

          // Process each message
          for (const message of messages) {
            try {
              // Here you would add your business logic
              console.log(`Processing document ${message.document_id} change: ${message.change_type}`)
              
              // Delete the message after processing
              await this.deleteMessage(message.msg_id)
              console.log(`Message ${message.msg_id} processed and deleted`)
            } catch (error) {
              console.error(`Failed to process message ${message.msg_id}:`, error)
              if (onError) onError(error as Error)
            }
          }
        }
      } catch (error) {
        console.error('Polling consumer error:', error)
        if (onError) onError(error as Error)
      }

      if (isRunning) {
        setTimeout(poll, intervalMs)
      }
    }

    // Start polling
    poll()

    // Return stop function
    return () => {
      isRunning = false
    }
  }
} 